import { inject, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { ErrorComponent } from '@c1/error/error.component';
import { LoginComponent } from '@c1/login/login.component';
import { UserAggrementComponent } from '@c1/user-aggrement/user-aggrement.component';
import { Features, TabNames, UserAccessType } from '@const';
import { AuthGuard } from '@core1/directives/auth-guard.service';
import { AdminPanelGuard } from '@g/admin-panel.guard';
import { EditionGuard } from '@g/edition.guard';
import { IncomeMachineGuard } from '@g/income-machine.guard';
import { NavigationService } from '@s/navigation.service';
import { ObservableService } from '@s/observable.service';

const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: 'login', component: LoginComponent },
  { path: 'error', component: ErrorComponent },
  { path: 'user-agreement', component: UserAggrementComponent },
  {
    path: 'app',
    loadComponent: () => import('@containers/pwa-entry/pwa-entry.component').then((m) => m.PwaEntryComponent),
  },

  {
    path: 'home',
    loadComponent: () => import('@containers/home-page/home-page.component').then((m) => m.HomePageComponent),
  },
  {
    path: 'license-agreement',
    loadChildren: () =>
      import('./containers/license-agreement/license-agreement.module').then((m) => m.LicenseAgreementModule),
  },
  {
    path: 'security-and-privacy',
    loadChildren: () =>
      import('./containers/security-and-privacy/security-and-privacy.module').then((m) => m.SecurityAndPrivacyModule),
  },
  {
    path: 'release-notes',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./containers/release-notes/release-notes.component').then((m) => m.ReleaseNotesComponent),
      },
      {
        path: 'guides/:demoId',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./containers/release-notes/walkthrough/walkthrough.component').then((m) => m.WalkthroughComponent),
      },
    ],
  },
  {
    path: 'disclaimer',
    loadChildren: () => import('./containers/disclaimer/disclaimer-page.module').then((m) => m.DisclaimerPageModule),
    canActivate: [EditionGuard],
    data: { feature: Features.Disclaimer },
  },
  {
    path: 'bull-call-spread',
    loadChildren: () =>
      import('./containers/bull-call-spread/bull-call-spread.module').then((m) => m.BullCallSpreadModule),
    canActivate: [EditionGuard],
    data: { feature: Features.BullCallSpread },
  },
  {
    path: 'wheel',
    loadChildren: () => import('./main/main.module').then((m) => m.MainModule),
    canActivate: [EditionGuard],
    data: { feature: Features.Wheel },
  },
  {
    path: 'powerx',
    loadChildren: () => import('./main-v2/main-v2.module').then((m) => m.MainV2Module),
    canActivate: [EditionGuard],
    data: { feature: Features.PowerX },
  },
  {
    path: 'ui-templates',
    loadChildren: () =>
      import('./containers/components-docs/components-docs.module').then((m) => m.ComponentsDocsModule),
  },
  {
    path: 'mm-trades',
    loadComponent: () =>
      import('./containers/spreadsheet-v2/spreadsheet-v2.component').then((m) => m.SpreadsheetV2Component),
    canActivate: [EditionGuard],
    data: { feature: Features.MMTrades },
  },
  {
    path: 'options-guy',
    loadComponent: () => import('./containers/rob-trades/rob-trades.component').then((m) => m.RobTradesComponent),
    canActivate: [EditionGuard],
    data: { feature: Features.RobTrades },
  },
  {
    path: 'asset-correlation',
    loadChildren: () =>
      import('./containers/asset-correlation/asset-correlation.module').then((m) => m.AssetCorrelationModule),
    canActivate: [EditionGuard],
    data: { feature: Features.AssetCorrelation },
  },
  {
    path: 'earnings-analysis',
    loadChildren: () =>
      import('./containers/earnings-analysis/earnings-analysis.module').then((m) => m.EarningsAnalysisModule),
    canActivate: [EditionGuard],
    data: { feature: Features.EarningsAnalysis },
  },
  {
    path: 'calendars',
    loadChildren: () =>
      import('@containers/combined-calendars/combined-calendars.module').then((m) => m.CombinedCalendarsModule),
    canActivate: [EditionGuard],
    data: { feature: null },
  },
  {
    path: 'income-machine',
    loadComponent: () =>
      import('./containers/income-machine/income-machine.component').then((m) => m.IncomeMachineComponent),
    canActivate: [EditionGuard, IncomeMachineGuard],
    data: { feature: Features.IncomeMachine },
  },
  {
    path: 'winning-strategy-selector',
    loadComponent: () =>
      import('./containers/winning-strategy-selector/winning-strategy-selector.component').then(
        (m) => m.WinningStrategySelectorComponent,
      ),
    canActivate: [EditionGuard],
    data: { feature: Features.WinningStrategySelector },
  },
  {
    path: '4-wheeler-and-earnings-vip-calculator',
    loadComponent: () =>
      import('./containers/wheeler-and-earnings-calculator/wheeler-and-earnings-calculator.component').then(
        (m) => m.WheelerAndEarningsCalculatorComponent,
      ),
    canActivate: [EditionGuard],
    data: { feature: Features.WheelerAndEarningsCalculator },
  },
  {
    path: 'wealth-os-dashboard',
    canActivate: [EditionGuard],
    loadChildren: () =>
      import('./containers/wealth-os-dashboard/wealth-os-dashboard.module').then((m) => m.WealthOsDashboardModule),
    data: { feature: Features.WealthOsDashboard },
  },
  {
    path: 'dividends-tracker',
    canActivate: [EditionGuard],
    loadComponent: () =>
      import('./containers/dividends-tracker/dividends-tracker-root.component').then(
        (m) => m.DividendsTrackerRootComponent,
      ),
    data: { feature: Features.DividendsTracker },
  },
  {
    path: 'sp-500-dashboard',
    canActivate: [EditionGuard],
    loadComponent: () =>
      import('./containers/sp-500-dashboard/sp-500-dashboard-root.component').then(
        (m) => m.Sp500DashboardRootComponent,
      ),
    data: { feature: Features.Sp500Dashboard },
  },
  {
    path: 'portfolio-builder',
    canActivate: [EditionGuard],
    loadComponent: () =>
      import('./containers/portfolio-builder/portfolio-builder-root.component').then(
        (m) => m.PortfolioBuilderRootComponent,
      ),
    data: { feature: Features.PortfolioBuilder },
  },
  {
    path: 'edge-finder',
    canActivate: [EditionGuard],
    loadComponent: () =>
      import('./containers/edge-finder/edge-finder-root.component').then((m) => m.EdgeFinderRootComponent),
    data: { feature: Features.EdgeFinder },
  },
  {
    path: 'passive-scanner',
    canActivate: [EditionGuard],
    loadComponent: () =>
      import('./containers/dividends-scanner/dividends-scanner-root.component').then(
        (m) => m.DividendsScannerRootComponent,
      ),
    data: { feature: Features.DividendsScanner },
  },
  {
    path: 'trading-plan',
    loadComponent: () =>
      import('./containers/trading-plan-v2/trading-plan-v2-root.component').then((m) => m.TradingPlanV2RootComponent),
  },
  {
    // Legacy heatmap — no longer surfaced in the UI, kept accessible by direct route only.
    path: 'heatmap-prev',
    loadChildren: () => import('./containers/heatmap/heatmap.module').then((m) => m.HeatmapModule),
    canActivate: [EditionGuard],
    data: { feature: Features.Heatmap },
  },
  {
    path: 'heatmap',
    loadChildren: () => import('./containers/heatmap-v2/heatmap-v2.module').then((m) => m.HeatmapV2Module),
    canActivate: [EditionGuard],
    data: { feature: Features.Heatmap },
  },
  {
    path: 'admin-configuration',
    canActivate: [AdminPanelGuard],
    loadChildren: () =>
      import('./containers/admin-configuration/admin-configuration.module').then((m) => m.AdminConfigurationModule),
  },
  {
    path: 'rocky-dashboard',
    canActivate: [AdminPanelGuard],
    loadComponent: () =>
      import('./containers/rocky-dashboard/rocky-dashboard-root.component').then((m) => m.RockyDashboardRootComponent),
  },
  {
    path: 'asset-correlation-static',
    loadChildren: () =>
      import('./containers/asset-correlation-static/asset-correlation.module').then((m) => m.AssetCorrelationModule),
    canActivate: [EditionGuard],
    data: { feature: Features.AssetCorrelation },
  },
  {
    path: 'trading-log',
    loadChildren: () => import('./containers/trading-log/trading-log.module').then((m) => m.TradingLogModule),
    canActivate: [EditionGuard],
    data: { feature: Features.TradingLog },
  },
  {
    // Temporally remove TradingPanelGuard to allow access to the trading panel from multileg popup.
    path: 'trading-panel',
    canActivate: [EditionGuard],
    loadChildren: () => import('./containers/trading-panel/trading-panel.module').then((m) => m.TradingPanelModule),
    data: { feature: Features.TradingPanel },
  },
  {
    path: 'import-trades',
    loadChildren: () => import('./containers/import-trades/import-trades.module').then((m) => m.ImportTradesModule),
    canActivate: [EditionGuard],
    data: { feature: Features.ImportTrades },
  },
  {
    path: 'wtf',
    canActivate: [EditionGuard],
    loadChildren: () => import('./containers/wtf/wtf.module').then((m) => m.WtfModule),
    data: { feature: Features.Wtf },
  },
  {
    path: 'editions',
    loadChildren: () => import('./containers/editions-demo/demo-pages.module').then((m) => m.DemoPagesModule),
  },
  {
    path: 'stock-screeners',
    loadChildren: () => import('./containers/stock-screener/stock-screener.module').then((m) => m.StockScreenerModule),
    canActivate: [EditionGuard],
    data: { feature: Features.StockScreeners },
  },
  {
    path: 'shorting-stocks',
    pathMatch: 'full',
    canActivate: [EditionGuard],
    loadChildren: () =>
      import('./containers/short-selling-stocks/short-selling-stocks.module').then((m) => m.ShortSellingStocksModule),
    data: { feature: Features.ShortSellingStocks },
  },
  {
    path: 'shorting-stocks-scanner',
    canActivate: [EditionGuard],
    loadChildren: () =>
      import('./containers/shorting-stocks-scanner/shorting-stocks-scanner.module').then(
        (m) => m.ShortingStocksScannerModule,
      ),
    data: { feature: Features.ShortingStocksScanner },
  },
  {
    path: 'video-hub',
    loadComponent: () => import('./containers/video-hub/video-hub.component').then((m) => m.VideoHubComponent),
    loadChildren: () => import('./containers/video-hub/video-hub.routes').then((m) => m.routes),
  },
  {
    path: 'dividends-strategy',
    canActivate: [EditionGuard],
    loadComponent: () =>
      import('./containers/dividends-strategy/dividends-strategy-root.component').then(
        (m) => m.DividendsStrategyRootComponent,
      ),
    data: { feature: Features.DividendsStrategy },
  },
  {
    path: 'pxo-ai-chat',
    canActivate: [
      EditionGuard,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      () => {
        const navigationService = inject(NavigationService);
        const observableService = inject(ObservableService);
        const settings = observableService.mySettings.getValue();

        // show this test page only for admins, currently it is available only for testing on DEV
        // if not admin, redirect to home page
        return settings?.access_type === UserAccessType.Admin || navigationService.redirectToTab(TabNames.Home);
      },
    ],
    loadComponent: () =>
      import('@containers/pxo-ai-chat/pxo-ai-chat-root.component').then((m) => m.PxoAiChatRootComponent),
    data: { feature: null },
  },
  {
    path: 'dividend-capture-testing',
    canActivate: [EditionGuard],
    loadChildren: () =>
      import('./containers/dividend-capture-testing/dividend-capture-testing.module').then(
        (m) => m.DividendCaptureTestingModule,
      ),
    data: { feature: Features.DividendCaptureTesting },
  },
  {
    path: 'wealth-os-testing',
    canActivate: [EditionGuard],
    loadChildren: () =>
      import('./containers/wealth-os-testing/wealth-os-testing.module').then((m) => m.WealthOsTestingModule),
    data: { feature: Features.WealthOsDashboard },
  },
  {
    path: 'brokerage-testing',
    canActivate: [AdminPanelGuard],
    loadChildren: () =>
      import('./containers/brokerage-testing/brokerage-testing.module').then((m) => m.BrokerageTestingModule),
    data: { feature: null },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
